import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Stepper, 
  Step, 
  StepLabel, 
  TextField, 
  FormControl, 
  FormControlLabel, 
  FormGroup, 
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Radio,
  RadioGroup,
  Divider,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import CountrySelect from '../../shared/CountrySelect';
import {
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Send as SendIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

// Import file upload component
import FileUploadField from './FileUploadField';

// Import enhanced form stepper for consistent design
import { FormStepperEnhanced, FormStep } from '../../shared/forms';

const FormRenderer = (props) => {
  // Directly extract formDefinition from props
  // This allows us to access the raw props object for debugging
  
  const { onSubmit, initialValues = {}, readOnly = false } = props;
  // formDefinition is extracted below after debugging
  const [activeSection, setActiveSection] = useState(0);
  const [formData, setFormData] = useState(initialValues);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Extract formDefinition from props.formDefinition or props directly
  // This handles the case where the component might be receiving the whole response object
  const rawFormDefinition = props.formDefinition || props;
  
  // Raw form definition received check
  
  // Handle the specific server response structure based on the provided example
  const actualFormDefinition = 
    // Case 1: Data is in data.formDefinition (exact server structure)
    rawFormDefinition?.data?.formDefinition ||
    // Case 2: The object itself is a form definition (has sections, title, etc.)
    (rawFormDefinition?.sections ? rawFormDefinition : null) ||
    // Case 3: It's in formDefinition property
    rawFormDefinition?.formDefinition ||
    // Case 4: First item in formDefinitions array
    (rawFormDefinition?.formDefinitions?.length > 0 ? rawFormDefinition.formDefinitions[0] : null) ||
    // Default fallback
    rawFormDefinition ||
    {};
  
  // Define state for form validity
  const [isValidForm, setIsValidForm] = useState(true);
  const [formError, setFormError] = useState(null);
  
  // Validate a single field
  const validateField = (field, value) => {
    // Check conditional display first
    if (field.conditionalDisplay && field.conditionalDisplay.length > 0) {
      let conditionallyHidden = false;
      
      for (const condition of field.conditionalDisplay) {
        const sourceValue = formData[condition.sourceField];
        let conditionMet = false;
        
        switch (condition.operator) {
          case 'equals':
            conditionMet = sourceValue === condition.value;
            break;
          case 'notEquals':
            conditionMet = sourceValue !== condition.value;
            break;
          case 'contains':
            conditionMet = sourceValue && sourceValue.includes(condition.value);
            break;
          case 'notContains':
            conditionMet = !sourceValue || !sourceValue.includes(condition.value);
            break;
          case 'greaterThan':
            conditionMet = sourceValue > condition.value;
            break;
          case 'lessThan':
            conditionMet = sourceValue < condition.value;
            break;
          case 'isEmpty':
            conditionMet = sourceValue === undefined || sourceValue === null || sourceValue === '';
            break;
          case 'isNotEmpty':
            conditionMet = sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
            break;
          default:
            break;
        }
        
        if (conditionMet && condition.action === 'hide') {
          conditionallyHidden = true;
          break;
        } else if (!conditionMet && condition.action === 'show') {
          conditionallyHidden = true;
          break;
        }
      }
      
      if (conditionallyHidden) {
        return null; // No error for hidden fields
      }
    }
    
    // Check required fields
    if (field.required && 
        (value === undefined || 
         value === null || 
         value === '')) {
      return `${field.label} is required`;
    }
    
    // Apply additional validations
    if (field.validations && field.validations.length > 0) {
      for (const validation of field.validations) {
        if (validation.type === 'required' && validation.value &&
            (value === undefined || 
             value === null || 
             value === '')) {
          return validation.message || `${field.label} is required`;
        } else if (validation.type === 'minLength' && 
                   value && 
                   value.length < validation.value) {
          return validation.message || `Minimum length is ${validation.value} characters`;
        } else if (validation.type === 'maxLength' && 
                   value && 
                   value.length > validation.value) {
          return validation.message || `Maximum length is ${validation.value} characters`;
        } else if (validation.type === 'min' && 
                   value !== undefined && 
                   value < validation.value) {
          return validation.message || `Minimum value is ${validation.value}`;
        } else if (validation.type === 'max' && 
                   value !== undefined && 
                   value > validation.value) {
          return validation.message || `Maximum value is ${validation.value}`;
        } else if (validation.type === 'pattern' && 
                   value && 
                   !new RegExp(validation.value).test(value)) {
          return validation.message || `Invalid format`;
        } else if (validation.type === 'email' && 
                   validation.value && 
                   value && 
                   !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          return validation.message || `Invalid email address`;
        } else if (validation.type === 'url' && 
                   validation.value && 
                   value && 
                   !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i.test(value)) {
          return validation.message || `Invalid URL`;
        } else if (validation.type === 'phone' && 
                   validation.value && 
                   value && 
                   !/^(\+\d{1,3}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i.test(value)) {
          return validation.message || `Invalid phone number`;
        }
      }
    }
    
    return null; // No error
  };

  // Initialize form with default values from form definition - must be before any conditionals
  useEffect(() => {
    // Check if the form is valid (has sections)
    if (!actualFormDefinition || !actualFormDefinition.sections) {
      setIsValidForm(false);
      setFormError('Missing required form structure with sections');
      return;
    }
    
    // Collect default values
    const defaults = {};
    
    // Process each section and field to collect default values
    for (const section of actualFormDefinition.sections || []) {
      for (const field of section.fields || []) {
          if (field.defaultValue !== undefined) {
            defaults[field.name] = field.defaultValue;
          } else if (field.type === 'select' && field.options) {
            // For select fields, find default option if any
            const defaultOption = field.options.find(option => option.isDefault);
            if (defaultOption) {
              defaults[field.name] = defaultOption.value;
            }
          }
        }
      }
      
      // Merge with initial values
      setFormData(prev => ({
        ...defaults,
        ...initialValues,
        ...prev
      }));
  }, [actualFormDefinition, initialValues, rawFormDefinition]);
  
  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation error if fixed
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
    
    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };
  
  // Get sorted sections from form definition
  const sortedSections = actualFormDefinition.sections 
    ? [...actualFormDefinition.sections].sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
    
  // Validate the current section
  const validateSection = (sectionIndex) => {
    const section = sortedSections[sectionIndex];
    if (!section) return true;
    
    const errors = {};
    let isValid = true;
    
    for (const field of section.fields || []) {
      // Skip non-input fields
      if (['heading', 'paragraph', 'divider'].includes(field.type)) {
        continue;
      }
      
      // Skip hidden fields
      if (field.visibility?.isHidden) {
        continue;
      }
      
      // Check if the field should be hidden due to conditional logic
      let conditionallyHidden = false;
      if (field.conditionalDisplay && field.conditionalDisplay.length > 0) {
        for (const condition of field.conditionalDisplay) {
          const sourceValue = formData[condition.sourceField];
          let conditionMet = false;
          
          switch (condition.operator) {
            case 'equals':
              conditionMet = sourceValue === condition.value;
              break;
            case 'notEquals':
              conditionMet = sourceValue !== condition.value;
              break;
            case 'contains':
              conditionMet = sourceValue && sourceValue.includes(condition.value);
              break;
            case 'notContains':
              conditionMet = !sourceValue || !sourceValue.includes(condition.value);
              break;
            case 'greaterThan':
              conditionMet = sourceValue > condition.value;
              break;
            case 'lessThan':
              conditionMet = sourceValue < condition.value;
              break;
            case 'isEmpty':
              conditionMet = sourceValue === undefined || sourceValue === null || sourceValue === '';
              break;
            case 'isNotEmpty':
              conditionMet = sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
              break;
            default:
              break;
          }
          
          if (conditionMet && condition.action === 'hide') {
            conditionallyHidden = true;
            break;
          } else if (!conditionMet && condition.action === 'show') {
            conditionallyHidden = true;
            break;
          }
        }
      }
      
      if (conditionallyHidden) {
        continue;
      }
      
      // Check required fields
      if (field.required && 
          (formData[field.name] === undefined || 
           formData[field.name] === null || 
           formData[field.name] === '')) {
        errors[field.name] = `${field.label} is required`;
        isValid = false;
      }
      
      // Apply additional validations
      if (field.validations && field.validations.length > 0) {
        for (const validation of field.validations) {
          if (validation.type === 'required' && validation.value &&
              (formData[field.name] === undefined || 
               formData[field.name] === null || 
               formData[field.name] === '')) {
            errors[field.name] = validation.message || `${field.label} is required`;
            isValid = false;
          } else if (validation.type === 'minLength' && 
                     formData[field.name] && 
                     formData[field.name].length < validation.value) {
            errors[field.name] = validation.message || `Minimum length is ${validation.value} characters`;
            isValid = false;
          } else if (validation.type === 'maxLength' && 
                     formData[field.name] && 
                     formData[field.name].length > validation.value) {
            errors[field.name] = validation.message || `Maximum length is ${validation.value} characters`;
            isValid = false;
          } else if (validation.type === 'min' && 
                     formData[field.name] !== undefined && 
                     formData[field.name] < validation.value) {
            errors[field.name] = validation.message || `Minimum value is ${validation.value}`;
            isValid = false;
          } else if (validation.type === 'max' && 
                     formData[field.name] !== undefined && 
                     formData[field.name] > validation.value) {
            errors[field.name] = validation.message || `Maximum value is ${validation.value}`;
            isValid = false;
          } else if (validation.type === 'pattern' && 
                     formData[field.name] && 
                     !new RegExp(validation.value).test(formData[field.name])) {
            errors[field.name] = validation.message || `Invalid format`;
            isValid = false;
          } else if (validation.type === 'email' && 
                     validation.value && 
                     formData[field.name] && 
                     !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData[field.name])) {
            errors[field.name] = validation.message || `Invalid email address`;
            isValid = false;
          } else if (validation.type === 'url' && 
                     validation.value && 
                     formData[field.name] && 
                     !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i.test(formData[field.name])) {
            errors[field.name] = validation.message || `Invalid URL`;
            isValid = false;
          } else if (validation.type === 'phone' && 
                     validation.value && 
                     formData[field.name] && 
                     !/^(\+\d{1,3}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i.test(formData[field.name])) {
            errors[field.name] = validation.message || `Invalid phone number`;
            isValid = false;
          }
        }
      }
    }
    
    setValidationErrors(prev => ({
      ...prev,
      ...errors
    }));
    
    // Mark all fields in this section as touched if validation fails
    if (!isValid) {
      const newTouchedFields = {};
      for (const field of section.fields || []) {
        if (errors[field.name]) {
          newTouchedFields[field.name] = true;
        }
      }
      
      setTouchedFields(prev => ({
        ...prev,
        ...newTouchedFields
      }));
    }
    
    return isValid;
  };
  
  // Handle next section
  const handleNext = () => {
    // Validate current section before proceeding
    if (validateSection(activeSection)) {
      setActiveSection(prev => Math.min(prev + 1, sortedSections.length - 1));
    }
  };
  
  // Handle previous section
  const handlePrevious = () => {
    setActiveSection(prev => Math.max(prev - 1, 0));
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    // Validate all sections
    let isValid = true;
    for (let i = 0; i < sortedSections.length; i++) {
      if (!validateSection(i)) {
        isValid = false;
        setActiveSection(i);
        break;
      }
    }
    
    if (!isValid) return;
    
    // Submit form data
    if (onSubmit) {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Validate form data structure before submission
        
        // Ensure formData is actually an object
        if (!formData || typeof formData !== 'object' || Array.isArray(formData)) {
          throw new Error('Invalid form data structure: Form data must be an object');
        }
        
        // The form will be submitted with files directly, so we don't need to pre-process them
        // Just send the formData as is - the FormSubmissionHandler will handle the files properly
        const sanitizedData = { ...formData };

        // We no longer pre-process files here - they will be sent directly with the form submission
        // The backend handleS3Upload middleware will handle the file uploads
        
        // Simple sanitization - just ensure values are serializable
        Object.keys(sanitizedData).forEach(key => {
          const value = sanitizedData[key];
          
          // Don't convert File objects - we need them for the submission
          if (value instanceof File) {
            return; // Keep the File object as is
          }
          
          // Handle other special cases
          if (value === undefined) {
            sanitizedData[key] = null;
          } else if (typeof value === 'function') {
            delete sanitizedData[key]; // Remove functions
          } else if (typeof value === 'object' && value !== null && !(value instanceof Blob)) {
            // Handle nested objects that might have non-serializable values
            try {
              // Test if it can be serialized
              JSON.stringify(value);
            } catch (err) {
              // If serialization fails, convert to a simple object
              sanitizedData[key] = { 
                toString: String(value),
                nonSerializable: true
              };
            }
          }
        });
        
        // Important: await the result and capture it - this contains the submission result
        // that will be passed to onSubmitSuccess in FormSubmissionHandler
        const submissionResult = await onSubmit(sanitizedData);
        setSubmitSuccess(true);
      } catch (error) {
        setSubmitError(error.message || 'Failed to submit form');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  // Should field be displayed based on conditional logic
  const shouldDisplayField = (field) => {
    // Check if hidden by visibility settings
    if (field.visibility?.isHidden) {
      return false;
    }
    
    // Check conditional display logic (if any)
    if (field.conditionalDisplay && field.conditionalDisplay.length > 0) {
      for (const condition of field.conditionalDisplay) {
        const sourceValue = formData[condition.sourceField];
        let conditionMet = false;
        
        switch (condition.operator) {
          case 'equals':
            conditionMet = sourceValue === condition.value;
            break;
          case 'notEquals':
            conditionMet = sourceValue !== condition.value;
            break;
          case 'contains':
            conditionMet = sourceValue && sourceValue.includes(condition.value);
            break;
          case 'notContains':
            conditionMet = !sourceValue || !sourceValue.includes(condition.value);
            break;
          case 'greaterThan':
            conditionMet = sourceValue > condition.value;
            break;
          case 'lessThan':
            conditionMet = sourceValue < condition.value;
            break;
          case 'isEmpty':
            conditionMet = sourceValue === undefined || sourceValue === null || sourceValue === '';
            break;
          case 'isNotEmpty':
            conditionMet = sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
            break;
          default:
            break;
        }
        
        // Apply the condition action
        if (conditionMet) {
          if (condition.action === 'hide') {
            return false;
          }
        } else if (condition.action === 'show') {
          return false;
        }
      }
    }
    
    return true;
  };
  
  // Render form field based on type
  const renderField = (field) => {
    // Check if field should be displayed
    if (!shouldDisplayField(field)) {
      return null;
    }
    
    const isFieldInvalid = touchedFields[field.name] && validationErrors[field.name];
    const isDisabled = readOnly || field.visibility?.isReadOnly || field.disabled;
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'url':
      case 'number':
        return (
          <Box sx={{ width: '100%' }}>
            {field.label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5,
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {field.label}
                {field.required && <span style={{ color: 'error.main' }}> *</span>}
              </Typography>
            )}
            <TextField
              value={formData[field.name] || ''}
              onChange={(e) => handleFieldChange(field.name, field.type === 'number' ? Number(e.target.value) : e.target.value)}
              fullWidth
              required={field.required}
              error={Boolean(isFieldInvalid)}
              helperText={isFieldInvalid ? validationErrors[field.name] : field.description}
              placeholder={field.placeholder || ''}
              disabled={isDisabled}
              type={field.type === 'number' ? 'number' : field.type}
              InputProps={field.InputProps || {}}
              className={field.styling?.className}
              sx={{
                ...field.styling?.customStyles,
                '& .MuiFormHelperText-root': {
                  marginLeft: 0,
                  marginTop: '4px'
                }
              }}
            />
          </Box>
        );

      case 'phone':
        // Phone with integrated country code selection
        const countries = [
          { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '+20' },
          { code: 'AE', name: 'UAE', flag: '🇦🇪', dialCode: '+971' },
          { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
          { code: 'QA', name: 'Qatar', flag: '🇶🇦', dialCode: '+974' },
          { code: 'KW', name: 'Kuwait', flag: '🇰🇼', dialCode: '+965' },
          { code: 'BH', name: 'Bahrain', flag: '🇧🇭', dialCode: '+973' },
          { code: 'OM', name: 'Oman', flag: '🇴🇲', dialCode: '+968' },
          { code: 'JO', name: 'Jordan', flag: '🇯🇴', dialCode: '+962' },
          { code: 'LB', name: 'Lebanon', flag: '🇱🇧', dialCode: '+961' },
          { code: 'PS', name: 'Palestine', flag: '🇵🇸', dialCode: '+970' },
          { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
          { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
        ];
        
        const selectedCountryCode = formData[field.name]?.countryCode || 'EG';
        const selectedCountry = countries.find(c => c.code === selectedCountryCode) || countries[0];
        
        return (
          <Box sx={{ width: '100%' }}>
            {field.label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5,
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {field.label}
                {field.required && <span style={{ color: 'error.main' }}> *</span>}
              </Typography>
            )}
            <TextField
              fullWidth
              placeholder="Phone number"
              value={(formData[field.name]?.number || '')}
              onChange={(e) => {
                // Only allow numbers, spaces, hyphens, and parentheses
                const value = e.target.value;
                const sanitizedValue = value.replace(/[^0-9\s\-()]/g, '');
                
                const currentPhone = formData[field.name] || {};
                handleFieldChange(field.name, {
                  ...currentPhone,
                  number: sanitizedValue
                });
              }}
              type="tel"
              inputMode="tel"
              error={Boolean(isFieldInvalid)}
              disabled={isDisabled}
              required={field.required}
              helperText={isFieldInvalid ? validationErrors[field.name] : field.description}
              InputProps={{
                startAdornment: (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    pr: 1,
                    mr: 1
                  }}>
                    <Select
                      value={selectedCountryCode}
                      onChange={(e) => {
                        const currentPhone = formData[field.name] || {};
                        handleFieldChange(field.name, {
                          ...currentPhone,
                          countryCode: e.target.value
                        });
                      }}
                      variant="standard"
                      disabled={isDisabled}
                      disableUnderline={true}
                      sx={{
                        minWidth: 'auto',
                        '& .MuiSelect-select': {
                          paddingRight: '24px !important',
                          paddingLeft: 0,
                          paddingTop: 0,
                          paddingBottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          '&:focus': {
                            backgroundColor: 'transparent',
                          }
                        },
                        '& .MuiInput-underline:before': {
                          display: 'none !important',
                          border: 'none !important',
                        },
                        '& .MuiInput-underline:after': {
                          display: 'none !important',
                          border: 'none !important',
                        },
                        '& .MuiInput-underline:hover:before': {
                          display: 'none !important',
                          border: 'none !important',
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                          display: 'none !important',
                          border: 'none !important',
                        },
                        '& .MuiInput-underline.Mui-focused:after': {
                          display: 'none !important',
                          border: 'none !important',
                        },
                        '& .MuiSelect-icon': {
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }
                      }}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span style={{ fontSize: '1.2em' }}>{country.flag}</span>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {country.dialCode}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                ),
              }}
              sx={{
                '& .MuiFormHelperText-root': {
                  marginLeft: 0,
                  marginTop: '4px'
                }
              }}
            />
          </Box>
        );
        
      case 'currency':
        // Import currency utils for consistent handling
        const { isRTLCurrency, ensureCurrencySymbol } = require('../../../utils/currencyUtils');
        
        // Get currency symbol, ensuring it has a default
        const currencySymbol = ensureCurrencySymbol(field);
        const isArabicCurrency = isRTLCurrency(currencySymbol);
        
        return (
          <Box sx={{ width: '100%' }}>
            {field.label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5,
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {field.label}
                {field.required && <span style={{ color: 'error.main' }}> *</span>}
              </Typography>
            )}
            <TextField
              value={formData[field.name] || ''}
              onChange={(e) => {
                // Only allow numbers and decimal point
                const value = e.target.value.replace(/[^0-9.]/g, '');
                
                // Ensure only one decimal point
                const parts = value.split('.');
                const sanitizedValue = parts.length > 2 
                  ? `${parts[0]}.${parts.slice(1).join('')}`
                  : value;
                  
                handleFieldChange(field.name, sanitizedValue);
              }}
              fullWidth
              required={field.required}
              error={Boolean(isFieldInvalid)}
              helperText={isFieldInvalid ? validationErrors[field.name] : field.description}
              placeholder={field.placeholder || '0.00'}
              disabled={isDisabled}
              InputProps={{
                ...(field.InputProps || {}),
                startAdornment: (
                  <Box component="span" sx={{ mr: 1, color: 'text.secondary', fontWeight: 500 }}>
                    {currencySymbol}
                  </Box>
                ),
                inputMode: 'decimal'
              }}
              className={field.styling?.className}
              sx={{
                ...field.styling?.customStyles,
                '& .MuiFormHelperText-root': {
                  marginLeft: 0,
                  marginTop: '4px'
                }
              }}
            />
          </Box>
        );
      
      case 'textarea':
        return (
          <Box sx={{ width: '100%' }}>
            {field.label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5,
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {field.label}
                {field.required && <span style={{ color: 'error.main' }}> *</span>}
              </Typography>
            )}
            <TextField
              value={formData[field.name] || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              fullWidth
              multiline
              rows={4}
              required={field.required}
              error={Boolean(isFieldInvalid)}
              helperText={isFieldInvalid ? validationErrors[field.name] : field.description}
              placeholder={field.placeholder || ''}
              disabled={isDisabled}
              className={field.styling?.className}
              sx={{
                ...field.styling?.customStyles,
                '& .MuiFormHelperText-root': {
                  marginLeft: 0,
                  marginTop: '4px'
                }
              }}
            />
          </Box>
        );
      
      case 'select':
        return (
          <Box sx={{ width: '100%' }}>
            {field.label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5,
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {field.label}
                {field.required && <span style={{ color: 'error.main' }}> *</span>}
              </Typography>
            )}
            <FormControl 
              fullWidth 
              error={Boolean(isFieldInvalid)}
              required={field.required}
              disabled={isDisabled}
              className={field.styling?.className}
              sx={field.styling?.customStyles}
            >
              <Select
                value={formData[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                displayEmpty
              >
                {field.placeholder && (
                  <MenuItem value="" disabled>
                    {field.placeholder}
                  </MenuItem>
                )}
                {field.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {(isFieldInvalid || field.description) && (
                <Typography 
                  variant="caption" 
                  color={isFieldInvalid ? 'error' : 'text.secondary'}
                  sx={{ mt: 0.5 }}
                >
                  {isFieldInvalid ? validationErrors[field.name] : field.description}
                </Typography>
              )}
            </FormControl>
          </Box>
        );
      
      case 'multiselect':
        return (
          <Box sx={{ width: '100%' }}>
            {field.label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5,
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {field.label}
                {field.required && <span style={{ color: 'error.main' }}> *</span>}
              </Typography>
            )}
            <FormControl 
              fullWidth 
              error={Boolean(isFieldInvalid)}
              required={field.required}
              disabled={isDisabled}
              className={field.styling?.className}
              sx={field.styling?.customStyles}
            >
              <Select
                multiple
                value={formData[field.name] || []}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>{field.placeholder || 'Select options'}</em>;
                  }
                  return selected.map(value => {
                    const option = field.options?.find(opt => opt.value === value);
                    return option?.label || value;
                  }).join(', ');
                }}
              >
                {field.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {(isFieldInvalid || field.description) && (
                <Typography 
                  variant="caption" 
                  color={isFieldInvalid ? 'error' : 'text.secondary'}
                  sx={{ mt: 0.5 }}
                >
                  {isFieldInvalid ? validationErrors[field.name] : field.description}
                </Typography>
              )}
            </FormControl>
          </Box>
        );
      
      case 'radio':
        return (
          <FormControl 
            component="fieldset" 
            error={Boolean(isFieldInvalid)}
            required={field.required}
            disabled={isDisabled}
            fullWidth
            className={field.styling?.className}
            sx={field.styling?.customStyles}
          >
            <Typography 
              variant="subtitle2" 
              color={isFieldInvalid ? 'error' : 'text.primary'}
            >
              {field.label}{field.required ? ' *' : ''}
            </Typography>
            <RadioGroup
              value={formData[field.name] || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
            >
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {(isFieldInvalid || field.description) && (
              <Typography 
                variant="caption" 
                color={isFieldInvalid ? 'error' : 'text.secondary'}
                sx={{ mt: 0.5 }}
              >
                {isFieldInvalid ? validationErrors[field.name] : field.description}
              </Typography>
            )}
          </FormControl>
        );
      
      case 'checkbox':
        return (
          <FormControl 
            component="fieldset" 
            error={Boolean(isFieldInvalid)}
            required={field.required}
            disabled={isDisabled}
            fullWidth
            className={field.styling?.className}
            sx={field.styling?.customStyles}
          >
            <Typography 
              variant="subtitle2" 
              color={isFieldInvalid ? 'error' : 'text.primary'}
            >
              {field.label}{field.required ? ' *' : ''}
            </Typography>
            <FormGroup>
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={Array.isArray(formData[field.name]) ? 
                        formData[field.name]?.includes(option.value) : 
                        formData[field.name] === option.value}
                      onChange={(e) => {
                        // Handle single checkbox
                        if (!field.options || field.options.length === 1) {
                          handleFieldChange(field.name, e.target.checked);
                          return;
                        }
                        
                        // Handle multiple checkboxes
                        const currentValues = Array.isArray(formData[field.name]) ? 
                          [...(formData[field.name] || [])] : 
                          [];
                          
                        if (e.target.checked) {
                          if (!currentValues.includes(option.value)) {
                            currentValues.push(option.value);
                          }
                        } else {
                          const index = currentValues.indexOf(option.value);
                          if (index > -1) {
                            currentValues.splice(index, 1);
                          }
                        }
                        
                        handleFieldChange(field.name, currentValues);
                      }}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
            {(isFieldInvalid || field.description) && (
              <Typography 
                variant="caption" 
                color={isFieldInvalid ? 'error' : 'text.secondary'}
                sx={{ mt: 0.5 }}
              >
                {isFieldInvalid ? validationErrors[field.name] : field.description}
              </Typography>
            )}
          </FormControl>
        );
      
      case 'date':
      case 'time':
      case 'datetime':
        return (
          <Box sx={{ width: '100%' }}>
            {field.label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0.5,
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {field.label}
                {field.required && <span style={{ color: 'error.main' }}> *</span>}
              </Typography>
            )}
            <TextField
              value={formData[field.name] || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              fullWidth
              required={field.required}
              error={Boolean(isFieldInvalid)}
              helperText={isFieldInvalid ? validationErrors[field.name] : field.description}
              type={field.type === 'datetime' ? 'datetime-local' : field.type}
              disabled={isDisabled}
              placeholder={field.placeholder || ''}
              className={field.styling?.className}
              sx={{
                ...field.styling?.customStyles,
                '& .MuiFormHelperText-root': {
                  marginLeft: 0,
                  marginTop: '4px'
                }
              }}
            />
          </Box>
        );
      
      case 'address':
        // A complex address field with street, city, state, zip
        return (
          <Box sx={{ width: '100%' }}>
            <Typography 
              variant="subtitle2" 
              color={isFieldInvalid ? 'error' : 'text.primary'} 
              sx={{ mb: 1 }}
            >
              {field.label}{field.required ? ' *' : ''}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  Street Address
                </Typography>
                <TextField
                  fullWidth
                  value={(formData[field.name]?.street || '')}
                  onChange={(e) => {
                    const currentAddress = formData[field.name] || {};
                    handleFieldChange(field.name, {
                      ...currentAddress,
                      street: e.target.value
                    });
                  }}
                  error={Boolean(isFieldInvalid)}
                  disabled={isDisabled}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  City
                </Typography>
                <TextField
                  fullWidth
                  value={(formData[field.name]?.city || '')}
                  onChange={(e) => {
                    const currentAddress = formData[field.name] || {};
                    handleFieldChange(field.name, {
                      ...currentAddress,
                      city: e.target.value
                    });
                  }}
                  error={Boolean(isFieldInvalid)}
                  disabled={isDisabled}
                />
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  State
                </Typography>
                <TextField
                  fullWidth
                  value={(formData[field.name]?.state || '')}
                  onChange={(e) => {
                    const currentAddress = formData[field.name] || {};
                    handleFieldChange(field.name, {
                      ...currentAddress,
                      state: e.target.value
                    });
                  }}
                  error={Boolean(isFieldInvalid)}
                  disabled={isDisabled}
                />
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  Zip Code
                </Typography>
                <TextField
                  fullWidth
                  value={(formData[field.name]?.zip || '')}
                  onChange={(e) => {
                    const currentAddress = formData[field.name] || {};
                    handleFieldChange(field.name, {
                      ...currentAddress,
                      zip: e.target.value
                    });
                  }}
                  error={Boolean(isFieldInvalid)}
                  disabled={isDisabled}
                />
              </Grid>
              
              <Grid item xs={12}>
                <CountrySelect
                  value={(formData[field.name]?.country || 'EG')}
                  onChange={(code) => {
                    const currentAddress = formData[field.name] || {};
                    handleFieldChange(field.name, {
                      ...currentAddress,
                      country: code
                    });
                  }}
                  label="Country"
                  error={Boolean(isFieldInvalid)}
                  disabled={isDisabled}
                />
              </Grid>
            </Grid>
            
            {(isFieldInvalid || field.description) && (
              <Typography 
                variant="caption" 
                color={isFieldInvalid ? 'error' : 'text.secondary'}
                sx={{ mt: 1, display: 'block' }}
              >
                {isFieldInvalid ? validationErrors[field.name] : field.description}
              </Typography>
            )}
          </Box>
        );
        
      case 'file':
      case 'image':
        // Create a ref for accessing the file objects during submission
        if (!field.fileRef) {
          field.fileRef = React.createRef();
        }
        
        return (
          <FileUploadField
            ref={field.fileRef}
            label={field.label}
            value={formData[field.name]}
            onChange={(files) => handleFieldChange(field.name, files)}
            required={field.required}
            error={Boolean(isFieldInvalid)}
            helperText={isFieldInvalid ? validationErrors[field.name] : field.description}
            multiple={field.settings?.allowMultiple}
            accept={field.type === 'image' ? 'image/*' : undefined}
            maxFiles={field.settings?.maxFiles}
            maxSize={field.settings?.maxSize}
            disabled={isDisabled}
            className={field.styling?.className}
            sx={field.styling?.customStyles}
            formSubmissionId={props.submissionId}
            isPublic={Boolean(props.shareToken)}
            shareToken={props.shareToken}
          />
        );
      
      case 'heading':
        return (
          <Typography 
            variant="h6"
            sx={{ 
              mt: 2, 
              mb: 1, 
              ...(field.styling?.customStyles || {})
            }}
            className={field.styling?.className}
          >
            {field.label}
          </Typography>
        );
      
      case 'paragraph':
        return (
          <Typography 
            variant="body1"
            sx={{ 
              my: 1, 
              ...(field.styling?.customStyles || {})
            }}
            className={field.styling?.className}
          >
            {field.description}
          </Typography>
        );
      
      case 'divider':
        return (
          <Divider 
            sx={{ 
              my: 2, 
              ...(field.styling?.customStyles || {})
            }}
            className={field.styling?.className}
          />
        );
      
      default:
        return (
          <Typography variant="body2" color="error">
            Unknown field type: {field.type}
          </Typography>
        );
    }
  };
  
  // Render form section
  const renderSection = (section) => {
    // Skip section if conditionally hidden
    if (section.conditionalDisplay && section.conditionalDisplay.length > 0) {
      for (const condition of section.conditionalDisplay) {
        const sourceValue = formData[condition.sourceField];
        let conditionMet = false;
        
        switch (condition.operator) {
          case 'equals':
            conditionMet = sourceValue === condition.value;
            break;
          case 'notEquals':
            conditionMet = sourceValue !== condition.value;
            break;
          case 'contains':
            conditionMet = sourceValue && sourceValue.includes(condition.value);
            break;
          case 'notContains':
            conditionMet = !sourceValue || !sourceValue.includes(condition.value);
            break;
          case 'greaterThan':
            conditionMet = sourceValue > condition.value;
            break;
          case 'lessThan':
            conditionMet = sourceValue < condition.value;
            break;
          case 'isEmpty':
            conditionMet = sourceValue === undefined || sourceValue === null || sourceValue === '';
            break;
          case 'isNotEmpty':
            conditionMet = sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
            break;
          default:
            break;
        }
        
        if (conditionMet && condition.action === 'hide') {
          return null;
        } else if (!conditionMet && condition.action === 'show') {
          return null;
        }
      }
    }
    
    return (
      <Box sx={{ mb: 3 }}>
        {section.title && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            {section.title}
          </Typography>
        )}
        
        {section.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {section.description}
          </Typography>
        )}
        
        <Grid container spacing={2}>
          {section.fields
            .sort((a, b) => a.order - b.order)
            .map((field) => (
              <Grid 
                item 
                xs={12} 
                md={field.width === '50%' ? 6 : 12} 
                key={field._id || field.name}
              >
                {renderField(field)}
              </Grid>
            ))}
        </Grid>
      </Box>
    );
  };
  
  // Show success message after submission
  if (submitSuccess) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {actualFormDefinition.submission?.successMessage || 'Form submitted successfully!'}
        </Typography>
        
        {actualFormDefinition.submission?.redirectUrl && (
          <Button 
            variant="contained" 
            href={actualFormDefinition.submission.redirectUrl}
            sx={{ mt: 2 }}
          >
            Continue
          </Button>
        )}
      </Paper>
    );
  }
  
  // Get visible sections based on conditional logic
  const visibleSections = sortedSections.filter(section => {
    if (!section.conditionalDisplay || section.conditionalDisplay.length === 0) {
      return true;
    }
    
    for (const condition of section.conditionalDisplay) {
      const sourceValue = formData[condition.sourceField];
      let conditionMet = false;
      
      switch (condition.operator) {
        case 'equals':
          conditionMet = sourceValue === condition.value;
          break;
        case 'notEquals':
          conditionMet = sourceValue !== condition.value;
          break;
        case 'contains':
          conditionMet = sourceValue && sourceValue.includes(condition.value);
          break;
        case 'notContains':
          conditionMet = !sourceValue || !sourceValue.includes(condition.value);
          break;
        case 'greaterThan':
          conditionMet = sourceValue > condition.value;
          break;
        case 'lessThan':
          conditionMet = sourceValue < condition.value;
          break;
        case 'isEmpty':
          conditionMet = sourceValue === undefined || sourceValue === null || sourceValue === '';
          break;
        case 'isNotEmpty':
          conditionMet = sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
          break;
        default:
          break;
      }
      
      if (conditionMet && condition.action === 'hide') {
        return false;
      } else if (!conditionMet && condition.action === 'show') {
        return false;
      }
    }
    
    return true;
  });

  // Configure steps for the enhanced stepper
  const steps = visibleSections.map((section, index) => ({
    label: section.title || `Step ${index + 1}`,
    icon: <DescriptionIcon />,
    optional: false,
    description: section.description || '',
    component: (
      <FormStep
        label={section.title || `Step ${index + 1}`}
        description={section.description || ''}
        icon={<DescriptionIcon />}
      >
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {section.fields
              .sort((a, b) => a.order - b.order)
              .map((field) => (
                <Grid 
                  item 
                  xs={12} 
                  md={field.width === '50%' ? 6 : 12} 
                  key={field._id || field.name}
                >
                  {renderField(field)}
                </Grid>
              ))}
          </Grid>
        </Box>
      </FormStep>
    )
  }));

  // If there's only one section, render it directly without the stepper
  if (visibleSections.length === 1) {
    return (
      <Box>
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}
        
        {renderSection(visibleSections[0])}
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            endIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Submit
          </Button>
        </Box>
      </Box>
    );
  }

  // Render the enhanced stepper for multi-step forms
  return (
    <Box>
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}
      
      <FormStepperEnhanced
        steps={steps}
        activeStep={activeSection}
        onStepChange={setActiveSection}
        onSubmit={handleSubmit}
        validateStep={async (stepIndex) => {
          // Validate current section
          const section = visibleSections[stepIndex];
          let isValid = true;
          const errors = {};
          const fieldsToTouch = {};
          
          for (const field of section.fields) {
            // Skip non-input fields
            if (['heading', 'paragraph', 'divider'].includes(field.type)) {
              continue;
            }
            
            const error = validateField(field, formData[field.name]);
            if (error) {
              errors[field.name] = error;
              fieldsToTouch[field.name] = true;
              isValid = false;
            }
          }
          
          // Mark all validated fields as touched so errors display
          setTouchedFields(prev => ({
            ...prev,
            ...fieldsToTouch
          }));
          
          // Set all validation errors at once
          setValidationErrors(prev => ({
            ...prev,
            ...errors
          }));
          
          return isValid;
        }}
        submitButtonText="Submit Form"
        submitButtonIcon={<SendIcon />}
        submitting={isSubmitting}
        magical={true}
        nextButtonText="Next"
        backButtonText="Previous"
      />
    </Box>
  );
};

export default FormRenderer;